import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import {
  Paginated,
  PaginationQuery,
  RawPaginationMeta,
  mapPaginationMeta,
} from '../../../../core/models/pagination.model';

export interface PaymentMethod {
  id: string;
  name: string;
  type: 'cash' | 'card' | 'qr' | 'bank';
  sort_order: number;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface PaymentMethodRequest {
  name: string;
  type: 'cash' | 'card' | 'qr' | 'bank';
  sort_order?: number;
  is_active?: boolean;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: Record<string, string[]>;
}

@Injectable({ providedIn: 'root' })
export class PaymentMethodsService {
  private readonly apiUrl = `${environment.apiUrl}/payment-methods`;

  constructor(private http: HttpClient) {}

  /** Full (unpaginated) list — used by dropdown consumers (e.g. transactions filter). */
  getAll(): Observable<ApiResponse<PaymentMethod[]>> {
    return this.http.get<ApiResponse<PaymentMethod[]>>(this.apiUrl, {
      params: new HttpParams().set('all', 'true'),
    });
  }

  /** Paginated list for the payment methods list page. */
  getPage(query: PaginationQuery = {}): Observable<Paginated<PaymentMethod>> {
    const params = new HttpParams()
      .set('page', String(query.page ?? 1))
      .set('perPage', String(query.perPage ?? 25));

    return this.http
      .get<ApiResponse<PaymentMethod[]> & { meta?: RawPaginationMeta }>(this.apiUrl, {
        params,
      })
      .pipe(
        map((res) => ({
          items: res.data ?? [],
          meta: mapPaginationMeta(res.meta, res.data?.length ?? 0),
        })),
      );
  }

  get(id: string): Observable<ApiResponse<PaymentMethod>> {
    return this.http.get<ApiResponse<PaymentMethod>>(`${this.apiUrl}/${id}`);
  }

  create(data: PaymentMethodRequest): Observable<ApiResponse<PaymentMethod>> {
    return this.http.post<ApiResponse<PaymentMethod>>(this.apiUrl, data);
  }

  update(id: string, data: Partial<PaymentMethodRequest>): Observable<ApiResponse<PaymentMethod>> {
    return this.http.put<ApiResponse<PaymentMethod>>(`${this.apiUrl}/${id}`, data);
  }

  delete(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${id}`);
  }

  toggleActive(id: string): Observable<ApiResponse<{ id: string; is_active: boolean }>> {
    return this.http.patch<ApiResponse<{ id: string; is_active: boolean }>>(
      `${this.apiUrl}/${id}/toggle-active`,
      {},
    );
  }
}
